import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuDownload, LuFile, LuFileText, LuFolderOpen, LuSearchX, LuTriangleAlert } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { useTranslation } from '../../../i18n/LanguageContext';
import { downloadFileAs } from '../../../utils/downloadFile';
import { EmptyState, LoadingRows, SearchField } from '../components/ProfileUi';
import AssignedDocumentViewer from './AssignedDocumentViewer';
import DocumentRow, { KIND_TONES } from './DocumentRow';

const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOC_TYPE = 'application/msword';

// legacyWord: eski ikili .doc; tarayıcıda çizilemez, Google'ın görüntüleyicisiyle açılır
const getKind = (docItem) => {
  const ext = (docItem.fileName || '').split('.').pop().toLowerCase();
  if (docItem.fileType === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (docItem.fileType === DOCX_TYPE || ext === 'docx') return 'word';
  if (docItem.fileType === DOC_TYPE || ext === 'doc') return 'legacyWord';
  return 'other';
};

const KIND_ICONS = { pdf: LuFileText, word: LuFileText, legacyWord: LuFileText, other: LuFile };
const toneOf = (docKind) => KIND_TONES[docKind === 'legacyWord' ? 'word' : docKind];

/**
 * Documents assigned to the user (Policies, Job Descriptions). A document
 * opens in AssignedDocumentViewer, which ends with the "I have read it"
 * confirmation; every row and the viewer offer a download.
 */
const AssignedDocumentList = ({ kind, documents, loading, error, isRead, onToggleRead }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.${key}`, vars);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);

  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const nameOf = (docItem) => docItem.name || label(`untitled.${kind}`);
  const filtered = documents.filter((d) => nameOf(d).toLocaleLowerCase('tr').includes(term)
    || (d.subtitle || '').toLocaleLowerCase('tr').includes(term));

  const download = (docItem) => downloadFileAs(docItem.fileUrl, docItem.fileName || nameOf(docItem));

  let list;
  if (loading) {
    list = <LoadingRows count={3} />;
  } else if (error) {
    list = <EmptyState compact icon={LuTriangleAlert} title={label('loadError')} />;
  } else if (filtered.length === 0) {
    list = (
      <EmptyState
        compact
        icon={term ? LuSearchX : LuFolderOpen}
        title={term ? label('noMatch', { term: searchTerm.trim() }) : label(`empty.${kind}`)}
      />
    );
  } else {
    list = (
      <ul className="tw-space-y-2">
        {filtered.map((docItem) => {
          const docKind = getKind(docItem);
          return (
            <DocumentRow
              key={docItem.id}
              icon={KIND_ICONS[docKind]}
              tone={toneOf(docKind)}
              title={nameOf(docItem)}
              onClick={() => setSelected(docItem)}
              meta={(docItem.version || docItem.subtitle) ? (
                <>
                  {docItem.version && <span className="tw-inline">v{docItem.version}</span>}
                  {docItem.subtitle && <span className="tw-inline">{docItem.subtitle}</span>}
                </>
              ) : null}
              badges={docItem.fileUrl ? (
                isRead(docItem.id)
                  ? <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{label('badges.read')}</Badge>
                  : <Badge tone="neutral">{label('badges.unread')}</Badge>
              ) : null}
              trailing={docItem.fileUrl ? (
                <button
                  type="button"
                  onClick={() => download(docItem)}
                  title={label('download')}
                  aria-label={label('downloadNamed', { name: nameOf(docItem) })}
                  className="tw-grid tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 tw-transition-colors hover:tw-border-teal-400/40 hover:tw-text-teal-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-text-teal-300"
                >
                  <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              ) : null}
            />
          );
        })}
      </ul>
    );
  }

  return (
    <div className="tw-space-y-3">
      <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={label(`search.${kind}`)} />
      {list}

      <AssignedDocumentViewer
        kind={kind}
        docItem={selected}
        docKind={selected ? getKind(selected) : undefined}
        title={selected ? nameOf(selected) : ''}
        isRead={Boolean(selected && isRead(selected.id))}
        onToggleRead={() => selected && onToggleRead(selected.id)}
        onDownload={() => selected && download(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

AssignedDocumentList.propTypes = {
  kind: PropTypes.oneOf(['policies', 'jobDescriptions']).isRequired,
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  isRead: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
};

export default AssignedDocumentList;
